import type { Asset, FbxAsset } from "building-types/entities/asset";
import type { BuiltInLabel } from "building-types/entities/labelTypes";
import type { PropInstanceHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import { getBuiltinProp } from "building-types/props/getBuiltinPropLibrary";
import type { PropAsset } from "building-types/props/PropAsset";
import { PropCategory } from "building-types/props/propCategoryTypes";
import type { Prop, PropMaterialOverrides, PropAttributes, PropAttributeTable } from "building-types/props/propTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import type { RWTable, Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey, materialSrcFromCacheKey } from "common/materials/materialSrc";
import { degreeToRadian } from "common/utils/math";
import type { PartialRecord } from "common/utils/types";
import uid from "common/utils/uid";
import type { AABB2 } from "geom/aabb2Types";
import type { AABB3 } from "geom/aabb3Types";
import type { BRep } from "geom/brep";
import { faceNew } from "geom/brep";
import { extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import { planeNew } from "geom/plane";
import {
  polyLine2AABB2,
  polyLine2Centroid,
  polyLine2Move,
  polyLine2Rotate,
  polyLine2Scale,
  polyLine2SetCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByAABB2 } from "geom/rect2";
import type { Vec3 } from "geom/vec3";
import { buildingEntityBaseNew } from "./utils/buildingEntityTools";

const materialSrcCachePrefix = "MATSRC_";

export class MergedPropLibrary {
  private importedAssets: Table<Asset>;
  private cachedImportedAssets: RWTable<PropAsset> = {};

  constructor(assets: Table<Asset>) {
    this.importedAssets = assets;
  }

  async get(id: string | number): Promise<PropAsset | undefined> {
    if (this.cachedImportedAssets[id]) {
      return this.cachedImportedAssets[id];
    }
    const importedAsset = this.importedAssets[id];
    if (importedAsset) {
      if (importedAsset.srcType === "FBX") {
        const fbxAsset = importedAsset as FbxAsset;
        this.cachedImportedAssets[id] = {
          ...fbxAsset,
          name: fbxAsset.id,
          hasSvg: true,
          imported: true,
        };
        return this.cachedImportedAssets[id];
      }
    }

    return await getBuiltinProp(id);
  }

  firstImportedAssetId(): string | null {
    return Object.keys(this.importedAssets)[0] ?? null;
  }
}

export function mergedPropLibraryNew(assets: Table<Asset>): MergedPropLibrary {
  return new MergedPropLibrary(assets);
}

export function propNew({
  room,
  src,
  x = 0,
  y = 0,
  z = 0,
  rotationDegrees = 0,
  scaleX = 1,
  scaleY = 1,
  scaleZ = 1,
  mirrorPropX,
  mirrorPropY,
  handle,
  handleAuxiliary,
  labels = {},
  floorMaterial,
  descriptor,
  catalogType,
  materialOverrides,
  useExteriorPalette,
  planOutline,
  alignedAABB3,
  useAlignmentCalc,
  alignmentX,
  alignmentY,
  entityInstanceType,
  floorPlanTag,
  floorPlanTagOffsetX,
  floorPlanTagOffsetY,
  isExterior = false,
}: {
  room: Room | null;
  src: string;
  useAlignmentCalc?: boolean;
  x?: number;
  y?: number;
  z?: number;
  rotationDegrees?: number;
  scaleX?: number;
  scaleY?: number;
  scaleZ?: number;
  mirrorPropX?: boolean;
  mirrorPropY?: boolean;
  handle?: PropInstanceHandle | RoomLayoutInstanceHandle;
  handleAuxiliary?: PropInstanceHandle;
  labels?: Table<boolean>;
  floorMaterial?: InteriorFloorMaterialOverride;
  descriptor?: ViewEntityDescriptor;
  catalogType: PropCatalogType;
  materialOverrides?: PropMaterialOverrides;
  useExteriorPalette?: boolean;
  planOutline?: PolyLine2;
  alignedAABB3?: AABB3;
  alignmentX?: number;
  alignmentY?: number;
  entityInstanceType?: EntityInstanceType;
  floorPlanTag?: string;
  floorPlanTagOffsetX?: number;
  floorPlanTagOffsetY?: number;
  isExterior?: boolean;
}): Prop {
  const instanceId = room?.instanceId;
  return {
    ...buildingEntityBaseNew("PROP", uid(), handle, entityInstanceType),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    room,
    src,
    x,
    y,
    z,
    rotationDegrees,
    scaleX,
    scaleY,
    scaleZ,
    mirrorPropX,
    mirrorPropY,
    handle,
    handleAuxiliary,
    labels,
    floorMaterial,
    descriptor,
    catalogType,
    materialOverrides,
    useExteriorPalette,
    planOutline,
    alignedAABB3,
    useAlignmentCalc,
    alignmentX,
    alignmentY,
    floorPlanTag,
    floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    isExterior,
  };
}

export function propCatalogItemNew({
  id,
  name,
  propAssetId,
  category = PropCategory.OTHER,
  floorPlanTag,
  showOnElevationsAndPerspectives,
  floorPlanTagOffsetX,
  floorPlanTagOffsetY,
  defaultVerticalOffset = 0,
  useExteriorPalette,
  isPlumbingArchitecturalProp,
  excludeFromFoundationPlanDimensions = false,
  isUtilityArchitecturalProp,
  isElectricalArchitecturalProp,
  hideTagOnElectricalPlan: drawOnlyElectical = false,
  takeoffDescription,
  propBoundOrientation,
  materialOverrides,
  labels = defaultPropLabels(showOnElevationsAndPerspectives),
  uniformScale = true,
  scaleX = 1,
  scaleY = 1,
  scaleZ = 1,
}: Omit<
  Partial<PropAttributes> & Pick<PropAttributes, "id" | "name" | "propAssetId">,
  "catalogItemType" | "entityType" | "sortIndex"
> & { showOnElevationsAndPerspectives?: boolean }): PropAttributes {
  return {
    catalogItemType: "PROP",
    entityType: "PROP",
    sortIndex: 0,
    id,
    name,
    propAssetId,
    category,
    floorPlanTag,
    floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    defaultVerticalOffset,
    useExteriorPalette,
    isPlumbingArchitecturalProp,
    excludeFromFoundationPlanDimensions,
    isUtilityArchitecturalProp,
    isElectricalArchitecturalProp,
    hideTagOnElectricalPlan: drawOnlyElectical,
    takeoffDescription,
    propBoundOrientation,
    materialOverrides,
    labels,
    uniformScale,
    scaleX,
    scaleY,
    scaleZ,
  };
}

export function defaultPropLabels(elevationAndPerspective: boolean = false): PartialRecord<BuiltInLabel, true> {
  const labels: PartialRecord<BuiltInLabel, true> = {
    addToElectricalPlan: true,
    addToFloorPlan: true,
    addToStructuralPlan: true,
    addToBrochurePlan: true,
    addToFloorMaterialsPlan: true,
    addToFloorSummaryPlan: true,
  };
  if (elevationAndPerspective) {
    labels.addToExteriorElevation = true;
    labels.addToPerspectiveView = true;
  }
  return labels;
}

export async function propPlanOutline(
  prop: Prop,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
): Promise<PolyLine2 | null> {
  const propAssetId = propCatalog[prop.src]?.propAssetId;
  if (propAssetId === undefined) {
    return null;
  }
  const propSrc = await mergedPropLibrary.get(propAssetId);
  if (!propSrc || !propSrc.hasSvg) {
    return null;
  }

  const bbox = { ...propSrc.boundingBox };

  if (prop.scaleX === -1) {
    const minx = bbox.maxX * -1;
    const maxx = bbox.minX * -1;
    bbox.minX = minx;
    bbox.maxX = maxx;
  }

  if (prop.scaleY === -1) {
    const miny = bbox.maxY * -1;
    const maxy = bbox.minY * -1;
    bbox.minY = miny;
    bbox.maxY = maxy;
  }

  return polyLine2Move(
    polyLine2Rotate(polyLine2Scale(rect2ByAABB2(bbox), GLTF_ASSET_SCALE), degreeToRadian(prop.rotationDegrees)),
    prop.x,
    prop.y,
  );
}

export async function propBoundingBoxBRep(
  prop: Prop,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
): Promise<BRep | null> {
  const propAssetId = propCatalog[prop.src]?.propAssetId;
  if (propAssetId === undefined) {
    return null;
  }
  const propSrc = await mergedPropLibrary.get(propAssetId);

  if (!propSrc) {
    return null;
  }

  const bbox = { ...propSrc.boundingBox };

  if (prop.scaleX === -1) {
    const minx = bbox.maxX * -1;
    const maxx = bbox.minX * -1;
    bbox.minX = minx;
    bbox.maxX = maxx;
  }

  if (prop.scaleY === -1) {
    const miny = bbox.maxY * -1;
    const maxy = bbox.minY * -1;
    bbox.minY = miny;
    bbox.maxY = maxy;
  }

  if (prop.scaleZ === -1) {
    const minz = bbox.maxZ * -1;
    const maxz = bbox.minZ * -1;
    bbox.minZ = minz;
    bbox.maxZ = maxz;
  }

  const loop = polyLine2SetCW(
    polyLine2Rotate(polyLine2Scale(rect2ByAABB2(bbox), GLTF_ASSET_SCALE), degreeToRadian(prop.rotationDegrees)),
  );

  const plane = planeNew(prop.x, prop.y, prop.z + bbox.minZ * GLTF_ASSET_SCALE, 1, 0, 0, 0, 1, 0);
  const face = faceNew([loop], plane);
  const extrusion = extrudeFaceAlongNormal(face, (bbox.maxZ - bbox.minZ) * GLTF_ASSET_SCALE);

  return extrusionToBRep(extrusion);
}

export async function propAABB2(
  prop: Prop,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
): Promise<AABB2 | null> {
  const rect = await propPlanOutline(prop, propCatalog, mergedPropLibrary);
  if (!rect) {
    return null;
  }
  return polyLine2AABB2(rect);
}

export function createPrefixedMaterialCacheKey(materialSrc: MaterialSrc): string {
  return materialSrcCachePrefix + materialCacheKey(materialSrc);
}

export function materialSrcFromPrefixedCacheKey(key: string): MaterialSrc {
  return materialSrcFromCacheKey(key.slice(materialSrcCachePrefix.length));
}

export function isPrefixedMaterialCacheKey(key: string): boolean {
  return key.startsWith(materialSrcCachePrefix);
}

export async function propCentroid(
  prop: Prop,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
): Promise<Vec3> {
  const rect = await propPlanOutline(prop, propCatalog, mergedPropLibrary);

  // in the event the prop has no plan outline, use the prop's position.
  // this may happen in cases where the prop is part of a catalog that is unavailable.
  if (!rect) {
    return [prop.x, prop.y, prop.z];
  }

  const centroid = polyLine2Centroid(rect);
  if (!centroid) {
    return [prop.x, prop.y, prop.z];
  }

  const [centroidX, centroidY] = centroid;
  return [centroidX, centroidY, prop.z];
}

const PropCategoryToFaceCategory: Record<PropCategory, FaceCategory> = {
  [PropCategory.APPLIANCES]: FaceCategory.PROP_APPLIANCES,
  [PropCategory.FIXTURES]: FaceCategory.PROP_FIXTURES,
  [PropCategory.FURNITURE]: FaceCategory.PROP_FURNITURE,
  [PropCategory.OTHER]: FaceCategory.PROP_OTHER,
  [PropCategory.FOLIAGE]: FaceCategory.PROP_FOLIAGE,
};

export function faceCategoryFromPropCategory(propCategory: PropCategory | undefined): FaceCategory {
  return PropCategoryToFaceCategory[propCategory ?? PropCategory.OTHER] ?? FaceCategory.PROP_OTHER;
}
